import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ProductsSection = () => {
  return (
    <section className="py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-24 animate-fade-in">
          <h2 className="text-6xl font-light mb-4">
            <span className="text-gray-900">Inspiring Technology.</span>{' '}
            <span className="gradient-text font-light">Outstanding results.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-20">
          {/* MCT Unit */}
          <div className="space-y-10 animate-slide-in-left">
            <div className="relative overflow-hidden rounded-3xl shadow-soft-xl group hover-lift bg-gradient-to-br from-gray-50 to-white p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/78aeklwf_image.png"
                alt="MCT Unit medical device"
                className="w-full h-[500px] object-contain group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div>
              <h3 className="text-4xl font-light mb-6 text-gray-900">MCT Unit®</h3>
              <p className="text-silver-600 leading-relaxed mb-6 font-light">
                The MCT Unit® revolutionizes regenerative medicine protocols through rapid, precise enhancement of existing treatments. Through precisely controlled electromagnetic and thermal energy application, it optimizes autologous product characteristics including growth factor concentration, ATP generation, and cellular performance metrics.
              </p>
              <p className="text-silver-600 leading-relaxed mb-8 font-light">
                Operating across a comprehensive temperature spectrum with multiple wavelength options, the system features intuitive one-touch protocol selection and customizable treatment programs for clinical flexibility.
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/jmn5q3w7_AF-dossier-MCT-PROTOCOLS-Digital.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white px-8 py-4 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                MCT Protocols
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </a>
            </div>
          </div>

          {/* MCT Kit */}
          <div className="space-y-10 animate-slide-in-right">
            <div className="relative overflow-hidden rounded-3xl shadow-soft-xl group hover-lift bg-gradient-to-br from-gray-50 to-white p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/si2tvlqa_image.png"
                alt="MCT Kit device"
                className="w-full h-[500px] object-contain group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div>
              <h3 className="text-4xl font-light mb-6 text-gray-900">MCT Kit®</h3>
              <p className="text-silver-600 leading-relaxed mb-6 font-light">
                The MCT Kit® represents a patented breakthrough in photothermal conditioning technology, featuring proprietary geometry and chemical composition exclusively developed for optimal therapeutic outcomes.
              </p>
              <p className="text-silver-600 leading-relaxed mb-8 font-light">
                Manufactured from pharmaceutical-grade polymers ensuring superior light scattering, transmittance, and optical properties, the device guarantees precise energy delivery to target tissues. Its innovative geometric design provides an exceptional surface-to-volume energy exposure ratio. The MCT Kit® accommodates 10 mL of any autologous preparate.
              </p>
              <div className="flex gap-6">
                <Link
                  to="/plasma"
                  className="inline-flex items-center gap-3 text-teal-500 hover:text-teal-600 font-light transition-all group"
                >
                  PRP protocol
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/exosomes"
                  className="inline-flex items-center gap-3 text-teal-500 hover:text-teal-600 font-light transition-all group"
                >
                  Exosomes protocol
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Product Info */}
        <div className="mt-24 grid md:grid-cols-2 gap-10">
          <div className="bg-gradient-to-br from-teal-50/50 to-white p-10 rounded-3xl shadow-soft hover-lift">
            <div className="flex items-start gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/78aeklwf_image.png"
                alt="MCT Unit front view"
                className="w-28 h-28 object-contain rounded-2xl shadow-soft bg-white p-2"
              />
              <div>
                <h4 className="text-2xl font-light mb-3 text-gray-900">MCT Unit</h4>
                <p className="text-silver-600 text-sm leading-relaxed font-light">
                  Advanced medical device enabling rapid enhancement of regenerative treatments through electromagnetic and thermal energy application with customizable protocols.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-silver-50/50 to-white p-10 rounded-3xl shadow-soft hover-lift">
            <div className="flex items-start gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/si2tvlqa_image.png"
                alt="MCT Kit cassette"
                className="w-28 h-28 object-contain rounded-2xl shadow-soft bg-white p-2"
              />
              <div>
                <h4 className="text-2xl font-light mb-3 text-gray-900">MCT Kit</h4>
                <p className="text-silver-600 text-sm leading-relaxed font-light">
                  Patented device exclusively developed for photothermal modulation with optimal optical properties and unique geometry for superior energy exposure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;